import { useMemo, useRef, useState } from "react";
import tw, { styled } from "twin.macro";
import { Combobox, Transition } from "@headlessui/react";

import { ReactComponent as SearchIcon } from "app/assets/icons/search-icon.svg";
import { ReactComponent as CaretDown } from "app/assets/icons/caret-down-alt.svg";
import { ReactComponent as Selected } from "app/assets/icons/check-outlined.svg";
import Text from "app/styles/Text";
import SelectOption from "app/models/selectOption";

interface Props {
  label?: string;
  placeholder: string;
  searchPlaceholder?: string;
  value?: string;
  options?: SelectOption[];
  onChange?: (i: SelectOption) => void;
}

export default function AutoComplete(props: Props) {
  const { value, onChange, options, label, placeholder, searchPlaceholder } =
    props;

  const selected = options?.find((o) => o.value === value);

  const searchBoxRef = useRef<HTMLInputElement | null>(null);

  const [query, setQuery] = useState("");

  const handleQueryFieldValueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.value.trim()?.length) {
      onChange && onChange({ value: "", label: "" });
    }
    setQuery(e.target.value);
  };

  const items = useMemo(() => {
    const otherItems = options
      ? options.filter((opt) => opt?.value !== selected?.value)
      : ([] as SelectOption[]);

    const items = selected ? [selected, ...otherItems] : otherItems;

    return items.filter(
      (item) =>
        item.value.toLowerCase().includes(query?.toLowerCase().trim()) ||
        item.label.toLowerCase().includes(query?.toLowerCase().trim())
    );
  }, [options, selected, query]);

  return (
    <div>
      {/* @ts-ignore */}
      <Combobox value={selected ?? ""} onChange={onChange}>
        {({ open }) => (
          <div css={tw`relative`}>
            <Text medium css={tw`mb-3`}>
              {label}
            </Text>

            {/* @ts-ignore */}
            <Combobox.Button as={SearchButton}>
              <InputContainer tw="flex justify-between">
                <Text subdued={!selected?.value}>
                  {selected?.label || placeholder}
                </Text>

                <CaretDown
                  width={20}
                  css={[
                    tw`flex-shrink-0 transition duration-200 ease-in-out`,
                    open && tw`rotate-[180deg]`,
                  ]}
                />
              </InputContainer>
            </Combobox.Button>

            {/* @ts-ignore */}
            <Transition
              as="div"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              beforeEnter={() => searchBoxRef.current?.focus()}
              afterLeave={() => setQuery("")}
            >
              {/* @ts-ignore */}
              <Combobox.Options as={Dropdown2}>
                <div tw="px-[20px] mb-[8px]">
                  <InputContainer>
                    {/* @ts-ignore */}
                    <Combobox.Button as={SearchButton}>
                      <SearchIcon />
                    </Combobox.Button>
                    {/* @ts-ignore */}
                    <Combobox.Input
                      placeholder={searchPlaceholder ?? "Search"}
                      value={query}
                      ref={searchBoxRef}
                      onChange={handleQueryFieldValueChange}
                      css={tw`w-full bg-transparent outline-none text-[1.8rem] placeholder:text-black32 dark:placeholder:text-white32 dark:text-white95`}
                    />

                    {!!query?.length && (
                      <button
                        tw="text-[1.6rem] font-semibold"
                        onClick={() => {
                          setQuery("");
                          searchBoxRef.current?.focus();
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </InputContainer>
                </div>

                {items?.map((item) => (
                  <Combobox.Option key={item.value} value={item}>
                    {({ selected, active }) => (
                      <>
                        <Option
                          key={item.value}
                          onClick={() => {
                            // setQuery(item.label);
                          }}
                        >
                          <div className="container">
                            <div tw="pt-[8px]">
                              <Text medium>{item.label}</Text>
                            </div>
                          </div>

                          {selected && <Selected className="selected-icon" />}
                        </Option>
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>
    </div>
  );
}

const InputContainer = styled.div`
  ${tw`w-full flex items-center gap-[9px] px-[16px] py-[10px] rounded-[4px] border border-[#0000001F] focus-within:border-black transition-colors`};

  ${tw`dark:border-white30 dark:focus-within:border-white`};
`;

const SearchButton = styled.div`
  svg {
    path {
      ${tw`dark:fill-transparent`};
    }
  }
`;

// const Dropdown = styled.div`
//   width: 100%;
//   z-index: 999;
//   position: absolute;
//   top: 74px;

//   li {
//     list-style-type: none;
//   }

//   ${tw`pt-[20px] dark:bg-[#000] dark:border-[rgba(255, 255, 255, 0.12)] max-h-[286px] overflow-scroll`};
//   ${tw` rounded-[8px] border border-transparent bg-white z-10 shadow-[0px 0px 4px rgba(0, 0, 0, 0.1), 0px 8px 40px rgba(0, 0, 0, 0.08)] mt-[10px]`};
// `;

const Dropdown2 = styled.div`
  width: 100%;
  z-index: 9999;
  position: absolute;
  left: 0;

  li {
    list-style-type: none;
  }

  ${tw`pt-[16px] dark:bg-[#000] dark:border-[rgba(255, 255, 255, 0.12)] max-h-[286px] overflow-y-auto`};
  ${tw` rounded-[10px] border border-borderHigh bg-white z-10 shadow-[0px 0px 7px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.08)] mt-[10px] bg-[#1dbf73]`};

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #b2b2b2;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #b2b2b2;
  }

  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: #b2b2b2 transparent;
`;

const Option = styled.div`
  ${tw`flex justify-between items-center px-[20px] py-[5px] hover:bg-greyScale05 dark:hover:bg-[#0D0F12] cursor-pointer`};

  .container {
    ${tw`flex h-[40px] gap-[14px]`}
  }

  .selected-icon {
    ${tw`w-[24px] h-[24px] flex-shrink-0 text-primary90 dark:text-prime`};
  }

  img {
    ${tw`w-16 h-16 self-center rounded-full object-contain`};
  }
`;
